import { useState, Fragment } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  MapPin,
  Scale,
  Receipt,
  Clock,
  Gavel,
  FileText,
  ArrowRight,
  ArrowLeft,
  Phone,
  Search,
  Users,
  Lock,
  BarChart3,
  AlertTriangle,
  Handshake,
  PieChart,
  ClipboardList,
  Plus,
  Minus,
  Check,
  Star,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  REVIEW_PRICE_LABEL,
  NEGOTIATION_PRICE_LABEL,
  NEGOTIATION_PRICING_NOTE_SHORT,
} from "@/lib/pricing";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "ClaimGuard — AI Invoice Reviews & Full-Service Negotiation" },
      {
        name: "description",
        content:
          "Professional invoice reviews backed by pricing research, regulations, and negotiation expertise. Get a premium findings report you can act on.",
      },
      {
        property: "og:title",
        content: "ClaimGuard — AI Invoice Reviews & Full-Service Negotiation",
      },
      {
        property: "og:description",
        content:
          "Upload your storage invoice. We investigate — checking local rates, laws, and documentation — and deliver a professional findings report.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://smart-invoice-ally.lovable.app/" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "ClaimGuard — AI Invoice Reviews & Full-Service Negotiation",
      },
      {
        name: "twitter:description",
        content:
          "Upload your storage invoice and receive a Professional AI Review comparing your charges to local market pricing and applicable laws.",
      },
    ],
    links: [{ rel: "canonical", href: "https://smart-invoice-ally.lovable.app/" }],
  }),
  component: LandingPage,
});

function SampleReportModal({ children }: { children: React.ReactNode }) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-md overflow-hidden rounded-2xl border border-border bg-white p-0 shadow-[0_24px_80px_-20px_rgba(15,27,61,0.25)]">
        <div className="px-6 pb-2 pt-8">
          <div className="mb-4 flex justify-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-savings-soft px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-savings">
              <Star className="h-3.5 w-3.5" />
              Exclusive Preview
            </span>
          </div>
          <DialogHeader className="space-y-3 text-center">
            <DialogTitle className="font-display text-2xl text-navy">
              Exclusive Preview
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              Our professional AI report is currently available only to active customers
              while we continue expanding the platform.
            </DialogDescription>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              Submit an invoice today to receive your Professional Findings Report.
            </DialogDescription>
          </DialogHeader>
        </div>
        <div className="flex flex-col gap-3 border-t border-border bg-secondary/30 px-6 py-5 sm:flex-row-reverse">
          <Link
            to="/submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-savings px-5 py-2.5 text-sm font-semibold text-savings-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
          >
            Submit Your Invoice
            <ArrowRight className="h-4 w-4" />
          </Link>
          <DialogClose asChild>
            <Button variant="outline" className="w-full sm:w-auto">
              Close
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LandingPage() {
  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <main id="main-content">
        <Hero />
        <TrustBar />
        <WhyThisMatters />
        <HowItWorks />
        <WhatYouReceive />
        <ReportInAction />
        <EveryReviewIncludes />
        <Pricing />
        <FAQ />
        <ClosingCta />
      </main>
      <SiteFooter />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* HERO                                                        */
/* ─────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-secondary/50">
      <div className="container-page grid gap-14 py-20 md:py-24 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
        {/* Copy */}
        <div className="flex flex-col justify-center">
          <h1 className="font-display text-[2.75rem] leading-[1.05] text-navy sm:text-5xl md:text-6xl lg:text-[4.25rem]">
            Know if your storage invoice is <em className="not-italic text-savings">fair</em> before
            you pay it.
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
            Upload your storage invoice and receive a Professional AI Review comparing your charges
            to local market pricing, applicable laws, and negotiation best practices — before you
            pay.
          </p>


          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-navy">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-trust" />
              Evidence-Based Review
            </span>
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 text-trust" />
              Local Market Comparisons
            </span>
            <span className="inline-flex items-center gap-2">
              <Handshake className="h-4 w-4 text-negotiate" />
              Optional Full-Service Negotiation
            </span>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-5">
            <Link
              to="/submit"
              className="group inline-flex items-center gap-2 rounded-lg bg-savings px-6 py-3.5 text-sm font-semibold text-savings-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-elevated"
            >
              Submit Your Invoice
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </Link>
            <SampleReportModal>
              <button
                type="button"
                className="text-sm font-semibold text-navy underline-offset-4 hover:underline"
              >
                View Sample Report →
              </button>
            </SampleReportModal>
          </div>

          <div className="mt-6 inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            Your information is secure and private.
          </div>
        </div>

        {/* Report preview */}
        <HeroReportPreview />
      </div>
    </section>
  );
}

function HeroReportPreview() {
  return (
    <div className="relative">
      <div className="pointer-events-none absolute -top-10 -right-10 h-56 w-56 rounded-full bg-trust-soft blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-savings-soft blur-3xl" />

      {/* Stacked depth cards behind main report */}
      <div className="pointer-events-none absolute inset-x-10 -bottom-5 h-full rounded-2xl bg-navy/5 blur-md" />
      <div className="pointer-events-none absolute inset-x-5 -bottom-3 h-full rounded-2xl border border-border bg-white/70 shadow-card" />

      <div className="relative overflow-hidden rounded-2xl border border-border bg-white shadow-[0_30px_80px_-24px_rgba(15,27,61,0.35)] ring-1 ring-black/[0.02] transition duration-500 hover:-translate-y-1 hover:shadow-[0_40px_100px_-24px_rgba(15,27,61,0.45)]">
        {/* Report header */}
        <div className="grid gap-4 border-b border-border p-6 sm:grid-cols-[1fr_auto] sm:items-start">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-navy text-white">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-navy">
                Professional Findings Report
              </div>
              <div className="mt-3 grid gap-4 text-[10px] uppercase tracking-wider text-muted-foreground sm:grid-cols-3">
                <div>
                  <div>Prepared For</div>
                  <div className="mt-1 text-sm normal-case tracking-normal text-navy">John Doe</div>
                </div>
                <div>
                  <div>File Number</div>
                  <div className="mt-1 text-sm normal-case tracking-normal text-navy">
                    FILE-2026-001248
                  </div>
                </div>
                <div>
                  <div>Date</div>
                  <div className="mt-1 text-sm normal-case tracking-normal text-navy">
                    May 12, 2026
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-savings/25 bg-savings-soft p-3 sm:min-w-[160px] sm:text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-savings">
              Total Supported Recovery
            </div>
            <div className="mt-1 font-display text-2xl leading-none text-savings">$1,286.00</div>
            <div className="mt-1 text-[10px] text-muted-foreground">23% of Invoice Total</div>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Executive Summary
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatBox label="Invoice Total" value="$5,586.00" />
            <StatBox label="Estimated Fair Payment" value="$4,300 – $4,850" tone="trust" />
            <StatBox label="Total Supported Recovery" value="$1,286.00" tone="savings" />
            <StatBox
              label="Negotiation Strength"
              value="STRONG"
              sub="High Leverage"
              tone="savings"
            />
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1.6fr]">
            <StatBox label="Evidence Score™" value="87 /100" sub="Strong" tone="trust" />
            <StatBox label="Confidence Score" value="92%" sub="Very High" tone="trust" />
            <div className="rounded-lg border border-border bg-secondary/40 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Recommendation
              </div>
              <p className="mt-2 text-xs leading-relaxed text-navy">
                This invoice contains multiple opportunities for reduction. Strong position to
                negotiate.
              </p>
            </div>
          </div>
        </div>

        {/* Navy footer strip */}
        <div className="grid gap-4 bg-navy px-6 py-4 text-white sm:grid-cols-4">
          <FooterStat icon={FileText} value="12" label="Report Sections" />
          <FooterStat icon={BarChart3} value="30+" label="Data Points Analyzed" />
          <FooterStat icon={Clock} value="10-60 Min" label="Typical Review Time" />
          <FooterStat icon={Lock} value="100%" label="Private & Secure" />
        </div>
      </div>
      <p className="relative mt-3 text-center text-[11px] text-muted-foreground">
        Illustrative example. Figures shown are not from a real claim.
      </p>
    </div>
  );
}

function Shield({ className }: { className?: string }) {
  return <ShieldCheck className={className} />;
}

function StatBox({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "trust" | "savings";
}) {
  const valueCls =
    tone === "savings" ? "text-savings" : tone === "trust" ? "text-navy" : "text-navy";
  return (
    <div className="rounded-lg border border-border bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1.5 font-display text-lg leading-none ${valueCls}`}>{value}</div>
      {sub && <div className="mt-1 text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function FooterStat({
  icon: Icon,
  value,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  label: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-white/70" />
      <div>
        <div className="text-sm font-semibold leading-tight">{value}</div>
        <div className="text-[10px] text-white/60">{label}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* TRUST BAR                                                   */
/* ─────────────────────────────────────────────────────────── */

const trustItems = [
  {
    icon: MapPin,
    title: "Local Market Comparisons",
    body: "We compare your charges to local ZIP code averages.",
    tone: "savings" as const,
  },
  {
    icon: Scale,
    title: "State Laws & Regulations",
    body: "We research applicable laws that may protect you.",
    tone: "trust" as const,
  },
  {
    icon: Receipt,
    title: "Fee & Charge Validation",
    body: "We identify questionable, duplicate, or unsupported charges.",
    tone: "navy" as const,
  },
  {
    icon: Clock,
    title: "Timeline Review",
    body: "We analyze storage duration, notices, and invoice timing.",
    tone: "fasttrack" as const,
  },
  {
    icon: Gavel,
    title: "Negotiation Opportunities",
    body: "We uncover leverage points to help you save more.",
    tone: "negotiate" as const,
  },
  {
    icon: FileText,
    title: "Professional Findings Report",
    body: "You receive a detailed report built for real results.",
    tone: "trust" as const,
  },

];

const toneIcon = (tone: "trust" | "savings" | "navy" | "fasttrack" | "negotiate") => {
  switch (tone) {
    case "savings":
      return "text-savings";
    case "navy":
      return "text-navy";
    case "fasttrack":
      return "text-fasttrack";
    case "negotiate":
      return "text-negotiate";
    default:
      return "text-trust";
  }
};

function TrustBar() {
  return (
    <section className="bg-secondary/40 py-10">
      <div className="container-page">
        <div className="rounded-2xl border border-border bg-white p-6 shadow-card md:p-8">
          <ul className="grid gap-6 md:grid-cols-3 lg:grid-cols-6">
            {trustItems.map((t) => (
              <li key={t.title} className="flex flex-col">
                <t.icon className={`h-6 w-6 ${toneIcon(t.tone)}`} />
                <div className="mt-3 text-sm font-semibold text-navy">{t.title}</div>
                <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{t.body}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* HOW IT WORKS                                                */
/* ─────────────────────────────────────────────────────────── */

const steps = [
  {
    icon: Phone,
    title: "Submit",
    body: "Take a photo, upload files, or email your invoice to your private address.",
    tone: "savings" as const,
  },
  {
    icon: Search,
    title: "Professional AI Review",
    body: "We analyze every charge, research applicable laws, and compare local market pricing.",
    tone: "trust" as const,
  },
  {
    icon: FileText,
    title: "Receive Your Report",
    body: "Your Professional Findings Report arrives with savings opportunities and supporting evidence.",
    tone: "negotiate" as const,
  },

  {
    icon: Users,
    title: "Negotiate & Save",
    body: "Negotiate yourself using our report or let our experts handle it for you.",
    tone: "fasttrack" as const,
  },
];

const stepBg = (tone: "trust" | "savings" | "navy" | "fasttrack" | "negotiate") => {
  switch (tone) {
    case "savings":
      return "bg-savings-soft text-savings";
    case "trust":
      return "bg-trust-soft text-trust";
    case "negotiate":
      return "bg-negotiate-soft text-negotiate";
    case "fasttrack":
      return "bg-fasttrack-soft text-fasttrack";
    default:
      return "bg-secondary text-navy";
  }
};

function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-secondary/40 py-24">
      <div className="container-page">
        <div className="mx-auto max-w-2xl text-center">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
            How It Works
          </div>
          <h2 className="mt-4 font-display text-4xl text-navy md:text-5xl">
            Your path to clarity in <em className="text-savings">4 simple steps</em>
          </h2>
        </div>

        <ol className="mt-16 grid gap-10 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] md:items-start md:gap-4">
          {steps.map((s, i) => (
            <Fragment key={s.title}>
              <li className="flex flex-col items-center text-center">
                <div className="relative">
                  <div
                    className={`flex h-20 w-20 items-center justify-center rounded-full ${stepBg(s.tone)}`}
                  >
                    <s.icon className="h-8 w-8" />
                  </div>
                  <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-navy text-xs font-bold text-white shadow">
                    {i + 1}
                  </span>
                </div>
                <div className="mt-5 font-display text-xl text-navy">{s.title}</div>
                <p className="mt-2 max-w-[220px] text-sm leading-relaxed text-muted-foreground">
                  {s.body}
                </p>
              </li>
              {i < steps.length - 1 && (
                <li className="hidden items-center justify-center pt-8 md:flex" aria-hidden>
                  <ArrowRight className="h-5 w-5 text-muted-foreground/50" />
                </li>
              )}
            </Fragment>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* WHAT YOU RECEIVE                                            */
/* ─────────────────────────────────────────────────────────── */

const deliverables = [
  {
    icon: ClipboardList,
    title: "Executive Summary",
    body: "A clear overview of findings, savings potential, and recommendations.",
    tone: "savings" as const,
  },
  {
    icon: PieChart,
    title: "Savings Analysis",
    body: "Estimated fair prices, savings opportunities, and negotiation targets.",
    tone: "savings" as const,
  },
  {
    icon: Scale,
    title: "State Laws & Regulations",
    body: "Applicable laws explained in plain English and how they support your case.",
    tone: "trust" as const,
  },
  {
    icon: BarChart3,
    title: "Market Comparison",
    body: "How your invoice compares to local storage rates in your ZIP code and surrounding areas.",
    tone: "negotiate" as const,
  },
  {
    icon: AlertTriangle,
    title: "Questionable Charges",
    body: "Overcharges, duplicate fees, excessive charges, and missing documentation identified.",
    tone: "fasttrack" as const,
  },
  {
    icon: Handshake,
    title: "Negotiation Strategy",
    body: "Actionable recommendations and leverage points to help you negotiate effectively.",
    tone: "negotiate" as const,
  },
];

function WhatYouReceive() {
  return (
    <section id="what-you-receive" className="bg-white py-24">
      <div className="container-page">
        <div className="mx-auto max-w-2xl text-center">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
            What You Receive
          </div>
          <h2 className="mt-4 font-display text-4xl text-navy md:text-5xl">
            A professional report that <em className="text-savings">helps you save</em>
          </h2>
        </div>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {deliverables.map((d) => (
            <div
              key={d.title}
              className="group flex gap-4 rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-1 hover:border-trust/30 hover:shadow-elevated"
            >
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${stepBg(d.tone)}`}
              >
                <d.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold text-navy">{d.title}</div>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{d.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* REPORT IN ACTION (navy band, carousel)                      */
/* ─────────────────────────────────────────────────────────── */

const reportSlides = [
  {
    title: "Executive Summary",
    body: (
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-savings">
          Total Supported Recovery
        </div>
        <div className="mt-2 font-display text-3xl text-savings">$1,286.00</div>
        <div className="mt-1 text-xs text-muted-foreground">23% of Invoice Total</div>
        <div className="mt-5 h-2 overflow-hidden rounded-full bg-secondary">
          <div className="h-full w-[23%] rounded-full bg-savings" />
        </div>
      </div>
    ),
  },
  {
    title: "Charge Breakdown",
    body: (
      <div>
        <div className="grid grid-cols-3 gap-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span>Description</span>
          <span>Amount</span>
          <span>Status</span>
        </div>
        <ul className="mt-3 space-y-2.5 text-xs">
          {[
            ["Storage - 30 Days", "$2,180.00", "Reduce Recommended", "warn"],
            ["Administrative Fee", "$435.00", "Above Market", "warn"],
            ["Environmental Fee", "$185.00", "Needs Documentation", "fasttrack"],
            ["Lock Cut Fee", "$95.00", "Within Market", "savings"],
            ["Insurance Fee", "$210.00", "Review Recommended", "warn"],
          ].map(([desc, amt, status, tone]) => (
            <li key={desc as string} className="grid grid-cols-3 items-center gap-3">
              <span className="text-navy">{desc}</span>
              <span className="text-navy">{amt}</span>
              <span
                className={`justify-self-start rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                  tone === "warn"
                    ? "bg-warn/15 text-warn"
                    : tone === "fasttrack"
                      ? "bg-fasttrack-soft text-fasttrack"
                      : "bg-savings-soft text-savings"
                }`}
              >
                {status}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-4 text-[11px] font-semibold text-trust">View All Charges →</div>
      </div>
    ),
  },
  {
    title: "Market Comparison",
    body: (
      <div>
        <div className="text-xs text-muted-foreground">Your Invoice vs Local Average</div>
        <div className="mt-6 grid grid-cols-2 items-end gap-8 px-4">
          <div className="flex flex-col items-center">
            <div className="text-[11px] font-semibold text-navy">$5,586</div>
            <div className="mt-1 h-28 w-full rounded-t-md bg-trust" />
            <div className="mt-2 text-[10px] text-muted-foreground">Your Invoice</div>
          </div>
          <div className="flex flex-col items-center">
            <div className="text-[11px] font-semibold text-navy">$4,300</div>
            <div className="mt-1 h-20 w-full rounded-t-md bg-trust/40" />
            <div className="mt-2 text-[10px] text-muted-foreground">Local Average</div>
          </div>
        </div>
        <div className="mt-4 text-center text-[11px] text-muted-foreground">
          You may be overpaying by <span className="font-semibold text-savings">$1,286 (23%)</span>
        </div>
      </div>
    ),
  },
  {
    title: "Laws & Regulations",
    body: (
      <div>
        <div className="text-[11px] font-semibold text-navy">Texas Property Code Sec. 59.015</div>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          Storage charges must be reasonable and not excessive.
        </p>
        <div className="mt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Why it matters:
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          May support a reduction in excessive storage fees.
        </p>
        <div className="mt-4 text-[11px] font-semibold text-trust">View All Laws →</div>
      </div>
    ),
  },
  {
    title: "Timeline Analysis",
    body: (
      <div>
        <ul className="space-y-3 text-xs">
          {[
            ["Vehicle stored", "Apr 14", "savings"],
            ["Notice sent", "Apr 21", "savings"],
            ["Required release date", "May 05", "warn"],
            ["Invoice issued", "May 12", "fasttrack"],
          ].map(([label, date, tone]) => (
            <li key={label as string} className="flex items-start gap-3">
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                  tone === "warn" ? "bg-warn" : tone === "fasttrack" ? "bg-fasttrack" : "bg-savings"
                }`}
              />
              <div className="flex flex-1 items-center justify-between">
                <span className="text-navy">{label}</span>
                <span className="text-muted-foreground">{date}</span>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-4 rounded-md bg-warn/10 px-3 py-2 text-[11px] text-warn">
          7 days past required release
        </div>
      </div>
    ),
  },
  {
    title: "Negotiation Opportunities",
    body: (
      <div>
        <ul className="space-y-2.5 text-xs">
          {[
            ["Storage rate reduction", "High", "savings"],
            ["Remove admin fee", "High", "savings"],
            ["Documentation request", "Medium", "fasttrack"],
            ["Late release credit", "Medium", "fasttrack"],
          ].map(([label, priority, tone]) => (
            <li key={label as string} className="flex items-center justify-between">
              <span className="text-navy">{label}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${
                  tone === "savings"
                    ? "bg-savings-soft text-savings"
                    : "bg-fasttrack-soft text-fasttrack"
                }`}
              >
                {priority}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-4 text-[11px] font-semibold text-trust">View Full Strategy →</div>
      </div>
    ),
  },
  {
    title: "Total Supported Recovery",
    body: (
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Range
        </div>
        <div className="mt-1 font-display text-2xl text-savings">$965 – $1,415</div>
        <div className="mt-4 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Conservative</span>
            <span className="font-semibold text-navy">$965</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Likely</span>
            <span className="font-semibold text-navy">$1,190</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Aggressive</span>
            <span className="font-semibold text-savings">$1,415</span>
          </div>
        </div>
        <div className="mt-5 h-2 overflow-hidden rounded-full bg-secondary">
          <div className="h-full w-[70%] rounded-full bg-gradient-to-r from-savings/60 to-savings" />
        </div>
      </div>
    ),
  },
];

function ReportInAction() {
  const [idx, setIdx] = useState(0);
  const visible = 3;
  const max = Math.max(0, reportSlides.length - visible);
  const prev = () => setIdx((v) => Math.max(0, v - 1));
  const next = () => setIdx((v) => Math.min(max, v + 1));

  return (
    <section id="report" className="relative overflow-hidden bg-navy py-24 text-white">
      <div className="pointer-events-none absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-trust/20 blur-3xl" />
      <div className="container-page relative">
        <div className="grid gap-10 md:grid-cols-[1fr_2fr] md:items-center">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/60">
              What's Inside Your Report
            </div>
            <h2 className="mt-4 font-display text-4xl leading-tight md:text-5xl">
              Built like a professional consulting report
            </h2>
            <p className="mt-4 text-white/70">
              Every section is designed to give you clarity, confidence, and leverage.
            </p>
            <SampleReportModal>
              <button
                type="button"
                className="mt-6 inline-flex items-center gap-1.5 border-b border-white/40 pb-0.5 text-sm font-semibold text-white hover:border-white"
              >
                View Sample Report <ArrowRight className="h-4 w-4" />
              </button>
            </SampleReportModal>
          </div>

          <div className="relative" role="group" aria-roledescription="carousel" aria-label="Report sections">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {reportSlides.slice(idx, idx + visible).map((s) => (
                <div
                  key={s.title}
                  className="rounded-xl border border-border bg-white p-5 text-navy shadow-elevated"
                >
                  <div className="text-[10px] font-bold uppercase tracking-wider text-navy">
                    {s.title}
                  </div>
                  <div className="mt-4">{s.body}</div>
                </div>
              ))}
            </div>

            <p aria-live="polite" className="sr-only">
              Showing sections {idx + 1} to {Math.min(idx + visible, reportSlides.length)} of{" "}
              {reportSlides.length}
            </p>

            <button
              type="button"
              onClick={prev}
              disabled={idx === 0}
              aria-label="Previous report sections"
              className="absolute -left-4 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-elevated transition hover:scale-105 disabled:opacity-40 lg:flex"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={next}
              disabled={idx === max}
              aria-label="Next report sections"
              className="absolute -right-4 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-elevated transition hover:scale-105 disabled:opacity-40 lg:flex"
            >
              <ArrowRight className="h-4 w-4" />
            </button>

            {/* Below lg the floating arrows are hidden — these keep every
                report section reachable on tablet and mobile. */}
            <div className="mt-6 flex items-center justify-center gap-4 lg:hidden">
              <button
                type="button"
                onClick={prev}
                disabled={idx === 0}
                aria-label="Previous report sections"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-navy shadow-elevated transition disabled:opacity-40"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <span className="text-xs text-white/70">
                {Math.min(idx + visible, reportSlides.length)} of {reportSlides.length}
              </span>
              <button
                type="button"
                onClick={next}
                disabled={idx === max}
                aria-label="Next report sections"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-navy shadow-elevated transition disabled:opacity-40"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* PRICING                                                     */
/* ─────────────────────────────────────────────────────────── */

function Pricing() {
  return (
    <section id="pricing" className="bg-white py-24">
      <div className="container-page">
        <div className="grid gap-10 md:grid-cols-[1fr_2fr] md:items-start">
          <div>
            <h2 className="font-display text-4xl leading-tight text-navy md:text-5xl">
              Simple pricing.
              <br />
              Real value.
            </h2>
            <p className="mt-4 text-muted-foreground">No hidden fees. No surprises.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* Invoice Review */}
            <PricingCard
              tone="savings"
              tag="Invoice Review"
              price={REVIEW_PRICE_LABEL}
              suffix="per invoice"
              features={[
                "Professional Findings Report",
                "Pricing & law analysis",
                "Market comparisons",
                "Negotiation recommendations",
              ]}
              cta="Submit Your Invoice"
              ctaHref="/submit"
            />
            {/* Full-Service Negotiation (highlighted) */}
            <div className="relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-negotiate px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                Most Popular
              </div>
              <PricingCard
                tone="negotiate"
                highlighted
                tag="Full-Service Negotiation"
                price={NEGOTIATION_PRICE_LABEL}
                suffix={NEGOTIATION_PRICING_NOTE_SHORT}
                features={[
                  "Everything in Review",
                  "Our experts negotiate for you",
                  "Status updates",
                  "We handle the back-and-forth",
                ]}
                cta="Request Full-Service Negotiation"
                ctaHref="/negotiation"
              />
            </div>
            {/* Sample */}
            <div className="flex flex-col justify-between rounded-2xl border border-border bg-secondary/40 p-6">
              <div>
                <div className="font-display text-xl text-navy">See a Sample Report</div>
                <p className="mt-3 text-sm text-muted-foreground">
                  See exactly what you'll receive before purchasing.
                </p>
              </div>
              <SampleReportModal>
                <button
                  type="button"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-trust"
                >
                  View Sample Report
                  <ArrowRight className="h-4 w-4" />
                </button>
              </SampleReportModal>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

function PricingCard({
  tone,
  tag,
  price,
  suffix,
  features,
  cta,
  ctaHref,
  highlighted,
}: {
  tone: "savings" | "negotiate";
  tag: string;
  price: string;
  suffix: string;
  features: string[];
  cta: string;
  ctaHref: string;
  highlighted?: boolean;
}) {
  const tagCls = tone === "savings" ? "text-savings" : "text-negotiate";
  const btnCls =
    tone === "savings"
      ? "bg-savings text-savings-foreground hover:opacity-90"
      : "bg-negotiate text-negotiate-foreground hover:opacity-90";
  const border = highlighted ? "border-negotiate/50 shadow-elevated" : "border-border";
  return (
    <div className={`flex h-full flex-col rounded-2xl border ${border} bg-white p-6`}>
      <div className={`text-sm font-semibold ${tagCls}`}>{tag}</div>
      <div
        className={
          price.startsWith("$")
            ? "mt-3 flex items-baseline gap-2"
            : "mt-3 flex flex-col gap-1"
        }
      >
        <span
          className={
            price.startsWith("$")
              ? "font-display text-4xl text-navy"
              : "font-display text-2xl leading-tight text-navy"
          }
        >
          {price}
        </span>
        <span className="text-xs text-muted-foreground">{suffix}</span>
      </div>
      <ul className="mt-5 space-y-2.5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-navy">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${tagCls}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to={ctaHref}
        className={`mt-6 inline-flex items-center justify-center rounded-lg px-4 py-3 text-sm font-semibold transition ${btnCls}`}
      >
        {cta}
      </Link>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* FAQ                                                         */
/* ─────────────────────────────────────────────────────────── */

const faqs = [
  {
    q: "How long does a review take?",
    a: "Most reviews complete within 10 to 60 minutes. Complex invoices can take longer.",
  },
  {
    q: "What types of invoices do you review?",
    a: "Storage invoices, towing invoices, impound fees, and related charges. We specialize in vehicle storage and recovery billing.",
  },
  {
    q: "How do I know if my invoice is negotiable?",
    a: "Your Professional Findings Report shows which charges sit above local market pricing or lack legal or documentary support. Those are the lines worth negotiating. We can't guarantee an outcome, but we only recommend negotiation when the evidence supports it.",
  },
  {
    q: "What if I already paid my invoice?",
    a: "We can still review it. Some overcharges can be recovered, and the report is useful evidence for a refund request.",
  },
  {
    q: "What happens after I purchase Full-Service Negotiation?",
    a: "A specialist reviews your findings, confirms pricing before any work begins, then contacts the vendor on your behalf and handles the back-and-forth. You receive status updates until the matter is closed.",
  },
  {
    q: "Is my information safe and private?",
    a: "Yes. Your invoice and personal details are encrypted and never sold. Only your reviewer can access your claim.",
  },
];


function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="bg-secondary/40 py-24">
      <div className="container-page">
        <div className="text-center">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="mx-auto mt-10 grid max-w-5xl gap-3 md:grid-cols-2">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            const panelId = `faq-panel-${i}`;
            const buttonId = `faq-trigger-${i}`;
            return (
              <div
                key={f.q}
                className="rounded-xl border border-border bg-white transition hover:border-trust/30"
              >
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left"
                >
                  <span className="font-semibold text-navy">{f.q}</span>
                  {isOpen ? (
                    <Minus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                </button>
                <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>
                  <p className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* CLOSING CTA                                                 */
/* ─────────────────────────────────────────────────────────── */

function ClosingCta() {
  return (
    <section className="bg-white py-16">
      <div className="container-page">
        <div className="relative overflow-hidden rounded-2xl bg-navy p-8 text-white md:p-12">
          <div className="grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-center">
            <div>
              <h2 className="font-display text-3xl leading-tight md:text-4xl">
                Ready to know if your invoice is fair?
              </h2>
              <p className="mt-3 text-white/70">
                Submit your invoice today and get the clarity you deserve.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link
                to="/submit"
                className="inline-flex items-center gap-2 rounded-lg bg-savings px-5 py-3 text-sm font-semibold text-savings-foreground shadow-sm transition hover:-translate-y-0.5"
              >
                Submit Your Invoice
                <ArrowRight className="h-4 w-4" />
              </Link>
              <SampleReportModal>
                <button
                  type="button"
                  className="inline-flex items-center rounded-lg border border-white/25 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  View Sample Report
                </button>
              </SampleReportModal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* WHY THIS MATTERS                                            */
/* ─────────────────────────────────────────────────────────── */

const whyItems = [
  {
    icon: BarChart3,
    title: "Above-market storage rates",
    body: "Daily rates that exceed local averages for your ZIP code.",
    tone: "trust" as const,
  },
  {
    icon: AlertTriangle,
    title: "Unsupported administrative charges",
    body: "Fees added without a clear basis in law, contract, or notice.",
    tone: "fasttrack" as const,
  },
  {
    icon: Receipt,
    title: "Duplicate fees",
    body: "Charges billed twice under different labels or line items.",
    tone: "fasttrack" as const,
  },
  {
    icon: ClipboardList,
    title: "Missing documentation",
    body: "Required notices, releases, or authorizations not provided.",
    tone: "trust" as const,
  },
  {
    icon: Clock,
    title: "Timeline discrepancies",
    body: "Storage days or key dates that don't line up with the record.",
    tone: "negotiate" as const,
  },
  {
    icon: Handshake,
    title: "Negotiation opportunities",
    body: "Leverage points that support a reasonable reduction.",
    tone: "savings" as const,
  },
];


function WhyThisMatters() {
  return (
    <section className="bg-white py-24">
      <div className="container-page">
        <div className="mx-auto max-w-2xl text-center">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
            Why customers submit their invoice
          </div>
          <h2 className="mt-4 font-display text-4xl text-navy md:text-5xl">
            What a professional review can <em className="text-savings">uncover</em>
          </h2>
          <p className="mt-4 text-muted-foreground">
            Most storage invoices contain at least one line worth a second look.
          </p>
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {whyItems.map((w) => (
            <div
              key={w.title}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition duration-300 hover:-translate-y-1 hover:border-trust/30 hover:shadow-elevated"
            >
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-xl ${stepBg(w.tone)} transition group-hover:scale-105`}
              >
                <w.icon className="h-5 w-5" />
              </div>
              <div className="mt-5 font-semibold text-navy">{w.title}</div>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* EVERY REVIEW INCLUDES                                       */
/* ─────────────────────────────────────────────────────────── */

const reviewIncludes = [
  "Local ZIP Code Market Comparison",
  "State Law & Regulation Research",
  "Charge Validation",
  "Timeline Analysis",
  "Documentation Review",
  "Negotiation Recommendations",
];

function EveryReviewIncludes() {
  return (
    <section className="bg-secondary/40 py-20">
      <div className="container-page">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-white p-8 shadow-card md:p-12">
          <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-trust-soft blur-3xl" />
          <div className="relative grid gap-10 md:grid-cols-[1fr_1.4fr] md:items-center">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-foreground">
                Every review includes
              </div>
              <h2 className="mt-4 font-display text-3xl leading-tight text-navy md:text-4xl">
                One review. Everything you need to negotiate with confidence.
              </h2>
              <Link
                to="/submit"
                className="group mt-6 inline-flex items-center gap-2 rounded-lg bg-savings px-5 py-3 text-sm font-semibold text-savings-foreground shadow-sm transition hover:-translate-y-0.5 hover:shadow-elevated"
              >
                Submit Your Invoice
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
              </Link>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {reviewIncludes.map((r) => (
                <li
                  key={r}
                  className="flex items-start gap-3 rounded-xl border border-border bg-secondary/30 p-4 text-sm text-navy transition hover:border-trust/30 hover:bg-white"
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-savings text-savings-foreground">
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="font-medium">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
